import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArchiveRestore,
  ArrowLeft,
  BookOpen,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, userHasPermission, type Subject } from "@eduviaos/shared";
import { SubjectEditorSheet } from "@/components/SubjectEditorSheet";
import { useArchiveSubject, useSubjects, useUnarchiveSubject } from "@/hooks/useAcademicCatalogue";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase H4 FE-1 — IA Catalogue → Subjects.
 *
 * DataTable CRUD page over `/api/academic/subjects`. Honours Iron
 * Rule #16: sticky header in scrollable region, zebra-stripes BANNED,
 * action column last with `…` dropdown, status pills via `<Badge>`,
 * action verbs via the BE `:archive` / `:unarchive` idempotent
 * endpoints.
 *
 * The page exists because the timetable solver, the catalogue
 * readiness aggregator, and the staff invite flow all read from this
 * collection — the IA needs a single screen to author and curate it
 * rather than going through the staff-invite wizard every time.
 */

const QUERY_KEY = "subjects-catalogue";

export function SubjectsCataloguePage() {
  const [qInput, setQInput] = React.useState("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingSubject, setEditingSubject] = React.useState<Subject | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState<Subject | null>(null);
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const { data, isLoading, isFetching, refetch, isError, error } = useSubjects({
    includeArchived,
  });
  const archiveMutation = useArchiveSubject();
  const unarchiveMutation = useUnarchiveSubject();

  const { user } = useAuth();
  const canWrite = React.useMemo(
    () =>
      user
        ? userHasPermission(
            user.role,
            user.permission_overrides ?? [],
            "manage_settings",
            user.permission_mode ?? "additive",
          )
        : false,
    [user],
  );

  const items = React.useMemo<Subject[]>(() => {
    const all = data?.items ?? [];
    const trimmed = qInput.trim().toLowerCase();
    if (!trimmed) return all;
    return all.filter(
      (subject) =>
        subject.code.toLowerCase().includes(trimmed) ||
        subject.name.toLowerCase().includes(trimmed) ||
        subject.aliases.some((alias) => alias.toLowerCase().includes(trimmed)) ||
        subject.applicable_grades.some((grade) => grade.toLowerCase().includes(trimmed)),
    );
  }, [data?.items, qInput]);

  const total = data?.items.length ?? 0;

  const openCreate = () => {
    setEditingSubject(null);
    setEditorOpen(true);
  };

  const openEdit = (subject: Subject) => {
    setEditingSubject(subject);
    setEditorOpen(true);
  };

  const handleSaved = (_saved: Subject) => {
    // SubjectEditorSheet fires its own toast.success; we just clear
    // local editing state so the next "New subject" click reopens
    // blank.
    setEditingSubject(null);
  };

  const handleConfirmArchive = async () => {
    if (!pendingArchive) return;
    setArchiveError(null);
    try {
      await archiveMutation.mutateAsync(pendingArchive.id);
      toast.success(`Archived "${pendingArchive.name}".`);
      setPendingArchive(null);
    } catch (raw) {
      if (raw instanceof ApiError && raw.message) {
        setArchiveError(raw.message);
      } else if (raw instanceof Error && raw.message) {
        setArchiveError(raw.message);
      } else {
        setArchiveError("Could not archive — please try again.");
      }
    }
  };

  const handleUnarchive = async (subject: Subject) => {
    try {
      await unarchiveMutation.mutateAsync(subject.id);
      toast.success(`Restored "${subject.name}".`);
    } catch (raw) {
      const message =
        raw instanceof Error && raw.message ? raw.message : "Could not restore — please try again.";
      toast.error(message);
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid={QUERY_KEY}>
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/catalogue">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Catalogue
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Subjects</PageHeaderHeading>
            <PageHeaderDescription>
              Institution-wide subject catalogue. Solver inputs, eligibility rows, and the timetable
              quick-generate sheet all read from this list.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh subjects"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button size="sm" onClick={openCreate} data-testid="subjects-create">
                <Plus aria-hidden="true" className="size-4" />
                New subject
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {!canWrite && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="subjects-readonly-banner"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            You're viewing this surface in read-only mode. Authoring subjects requires the{" "}
            <code className="text-foreground">manage_settings</code> permission.
          </span>
        </div>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load subjects
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(event) => setQInput(event.target.value)}
              placeholder="Filter by code, name, alias, or grade…"
              className="pl-8"
              aria-label="Filter subjects"
              data-testid="subjects-filter"
            />
          </div>
          {qInput ? (
            <Button variant="ghost" size="sm" onClick={() => setQInput("")}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <div className="flex items-center gap-2 pl-2">
            <Checkbox
              id="subjects-include-archived"
              checked={includeArchived}
              onCheckedChange={(value) => setIncludeArchived(value === true)}
            />
            <Label
              htmlFor="subjects-include-archived"
              className="text-muted-foreground text-xs font-normal"
            >
              Show archived
            </Label>
          </div>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[12%]">Code</TableHead>
                <TableHead className="w-[24%]">Name</TableHead>
                <TableHead className="w-[14%]">Kind</TableHead>
                <TableHead className="w-[18%]">Grades</TableHead>
                <TableHead className="w-[10%] text-right">Periods/wk</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[10%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={7} />
            ) : (
              <TableBody>
                {items.map((subject) => (
                  <SubjectRow
                    key={subject.id}
                    subject={subject}
                    canWrite={canWrite}
                    onEdit={openEdit}
                    onArchive={(target) => {
                      setArchiveError(null);
                      setPendingArchive(target);
                    }}
                    onUnarchive={(target) => void handleUnarchive(target)}
                    isUnarchiving={unarchiveMutation.isPending}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<BookOpen aria-hidden="true" />}
            title={isError ? "Couldn't load subjects" : qInput ? "No matches" : "No subjects yet"}
            description={
              isError
                ? "Refresh to retry — if it keeps failing, check the network."
                : qInput
                  ? "Try a different filter or clear it to see the full list."
                  : "Create a subject to start building your timetable. The eligibility matrix and solver inputs both read from this list."
            }
            action={
              isError ? (
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  <RefreshCw aria-hidden="true" className="size-4" />
                  Retry
                </Button>
              ) : qInput ? (
                <Button variant="outline" size="sm" onClick={() => setQInput("")}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filter
                </Button>
              ) : canWrite ? (
                <Button size="sm" onClick={openCreate}>
                  <Plus aria-hidden="true" className="size-4" />
                  New subject
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
              {qInput ? " (filtered)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">How it's used:</strong> the timetable solver requires
          at least one subject + section + teacher with eligibility before it can generate a grid.
          Archive instead of delete to keep historical timetables and roster entries renderable —
          archived subjects are hidden from new eligibility rows but never broken in old ones.
        </CardContent>
      </Card>

      <SubjectEditorSheet
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingSubject(null);
        }}
        existing={editingSubject}
        onSaved={handleSaved}
      />

      <Dialog
        open={Boolean(pendingArchive)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(null);
            setArchiveError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {pendingArchive?.name}?</DialogTitle>
            <DialogDescription>
              The subject is hidden from new eligibility rows, autocomplete, and the timetable
              quick-generate sheet. Existing eligibility and timetable slots referencing it remain
              intact — restore the subject any time to bring it back into authoring flows.
            </DialogDescription>
          </DialogHeader>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive subject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SubjectRowProps {
  subject: Subject;
  canWrite: boolean;
  onEdit: (subject: Subject) => void;
  onArchive: (subject: Subject) => void;
  onUnarchive: (subject: Subject) => void;
  isUnarchiving: boolean;
}

function SubjectRow({
  subject,
  canWrite,
  onEdit,
  onArchive,
  onUnarchive,
  isUnarchiving,
}: SubjectRowProps) {
  const isArchived = subject.archived_at != null;
  const grades =
    subject.applicable_grades.length === 0 ? "All grades" : subject.applicable_grades.join(", ");

  return (
    <TableRow data-testid={`subjects-row-${subject.id}`}>
      <TableCell>
        <code className="text-foreground text-xs">{subject.code}</code>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-foreground text-sm font-medium">{subject.name}</span>
          {subject.aliases.length > 0 ? (
            <span className="text-muted-foreground text-[11px]">
              aka {subject.aliases.join(", ")}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <span className="text-muted-foreground text-xs capitalize">
          {subject.kind.replace(/_/g, " ")}
          {subject.requires_lab_room ? " · lab" : ""}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-muted-foreground text-xs">{grades}</span>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <span className="text-foreground text-xs">{subject.default_periods_per_week}</span>
      </TableCell>
      <TableCell>
        <StatusPill status={isArchived ? "archived" : "active"} />
      </TableCell>
      <TableCell className="text-right">
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`Actions for ${subject.name}`}>
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onEdit(subject)}>
                <Pencil aria-hidden="true" className="size-4" />
                Edit
              </DropdownMenuItem>
              {isArchived ? (
                <DropdownMenuItem onSelect={() => onUnarchive(subject)} disabled={isUnarchiving}>
                  <ArchiveRestore aria-hidden="true" className="size-4" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => onArchive(subject)} className="text-destructive">
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
